import { useState, useEffect, createContext, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  X, ChevronRight, ChevronLeft, SkipForward, Check, Play, 
  Building2, Users, CalendarDays, Package, Carrot, UtensilsCrossed,
  Wine, Euro, Calculator, BarChart3, ClipboardCheck, Settings,
  Truck, FileText, Target, Sparkles, ArrowRight, CircleDot,
  Brain, MessageSquare, PartyPopper, RefreshCw, LayoutGrid, CreditCard
} from "lucide-react";
import { Button } from "./ui/button";
import GianlucaAvatar from "./GianlucaAvatar";

// Tutorial Context für globalen Zugriff
const TutorialContext = createContext(null);

export const useTutorial = () => useContext(TutorialContext);

// Gian-Luca - Der Tutorial Assistent
const ASSISTANT_NAME = "Gian-Luca";

// Tutorial Schritte - Logische Reihenfolge für neuen Gastronomen
const TUTORIAL_STEPS = [
  {
    id: "welcome",
    title: `Ciao! Ich bin ${ASSISTANT_NAME}`,
    description: `Willkommen bei MargeVio! Dein Arbeitsbereich ist jetzt in übersichtliche **Panels** gegliedert — Finanzen, Personal, Lager, Speisekarte & mehr. Ich führe dich Schritt für Schritt durch alle Module.`,
    icon: Sparkles,
    path: "/dashboard",
    action: null,
    tips: [
      "Startseite /dashboard = Panel-Übersicht mit 8 Gruppen",
      "Die Sidebar passt sich automatisch an das aktive Panel an",
      "Panels anpassen: Einstellungen → Panels"
    ],
    category: "start"
  },
  {
    id: "panel_uebersicht",
    title: "Dein Arbeitsbereich: Panels",
    description: "Jedes Panel bündelt thematisch zusammengehörende Module. Klickst du auf 'Personal', öffnet sich die Mitarbeiter-Seite und die Sidebar zeigt nur Personal-Module (Personal, MA-Zugang, Schichtplan). Mit 'Panel-Übersicht' kommst du jederzeit zurück.",
    icon: LayoutGrid,
    path: "/dashboard",
    action: "Schau dir die 8 Panels an — jedes zeigt Icon, Beschreibung und Anzahl der Module",
    tips: [
      "Übersicht, Finanzen, Personal, Lager & Ware, Speisekarte, Betrieb, Konto, Einstellungen",
      "Oben rechts: Quick-Access zum Abonnement (Konto-Panel)",
      "Reihenfolge & Inhalte frei konfigurierbar über Einstellungen → Panels"
    ],
    highlight: "panels-dashboard",
    category: "start"
  },
  {
    id: "unternehmen",
    title: "1. Unternehmensdaten einrichten",
    description: "Beginne mit deinen Firmendaten: Name, Adresse, Steuer-ID und Betriebsart. Diese Informationen erscheinen auf Exporten und Rechnungen.",
    icon: Building2,
    path: "/einstellungen?tab=unternehmen",
    action: "Fülle im Tab 'Unternehmen' die Grunddaten aus",
    tips: [
      "Der Firmenname erscheint auf allen Dokumenten",
      "PLZ & Stadt werden für das Wetter-Widget verwendet",
      "Die Betriebsart beeinflusst verfügbare Module"
    ],
    highlight: "unternehmen-tab",
    category: "grundlagen"
  },
  {
    id: "mitarbeiter_erstellen",
    title: "2. Ersten Mitarbeiter anlegen",
    description: "Erfasse deinen ersten Mitarbeiter mit Stundenlohn, Arbeitszeit und Sozialversicherungsdaten für die Kostenberechnung.",
    icon: Users,
    path: "/mitarbeiter",
    action: "Klicke auf 'Neuer Mitarbeiter' und fülle das Formular aus",
    tips: [
      "Stundenlohn × Wochenstunden × 4,33 = Monatsgehalt",
      "AG-Kosten werden automatisch berechnet (~21% auf Brutto)",
      "Lohnabrechnung kann als PDF heruntergeladen werden"
    ],
    highlight: "add-mitarbeiter-btn",
    category: "personal"
  },
  {
    id: "schichtplan",
    title: "3. Erste Schicht zuweisen",
    description: "Erstelle deinen Schichtplan und weise Mitarbeitern ihre Arbeitszeiten zu. Der Plan kann als PDF exportiert werden.",
    icon: CalendarDays,
    path: "/schichtplan",
    action: "Klicke auf einen Tag und weise eine Schicht zu",
    tips: [
      "Drag & Drop zum Verschieben von Schichten",
      "Farbcodes für verschiedene Schichttypen",
      "Wöchentliche Arbeitszeit wird automatisch summiert"
    ],
    highlight: "schichtplan-grid",
    category: "personal"
  },
  {
    id: "lieferant_erstellen",
    title: "4. Ersten Lieferanten anlegen",
    description: "Erfasse deine Lieferanten (Metro, Selgros, lokale Händler) mit Kontaktdaten und Lieferkonditionen.",
    icon: Truck,
    path: "/lieferanten",
    action: "Klicke auf 'Neuer Lieferant' und füge Großhändler hinzu",
    tips: [
      "KI erkennt bekannte Großhändler automatisch",
      "Lieferzeiten helfen bei der Bestellplanung",
      "Mindestbestellwert für Kostenoptimierung wichtig"
    ],
    highlight: "add-lieferant-btn",
    category: "einkauf"
  },
  {
    id: "zutat_erstellen",
    title: "5. Erste Zutat anlegen",
    description: "Erfasse Grundzutaten mit Einkaufspreisen. Diese bilden die Basis für deine Rezeptkalkulation.",
    icon: Carrot,
    path: "/zutaten",
    action: "Klicke auf 'Neue Zutat' und gib Preis pro Einheit ein",
    tips: [
      "Einheit genau wählen (kg, Liter, Stück)",
      "Lieferant zuordnen für Bestellübersicht",
      "Allergen-Kennzeichnung nicht vergessen!"
    ],
    highlight: "add-zutat-btn",
    category: "einkauf"
  },
  {
    id: "lager_produkt",
    title: "6. Lagerbestand einrichten",
    description: "Erfasse deinen aktuellen Warenbestand. Das System warnt bei Unterschreitung des Mindestbestands.",
    icon: Package,
    path: "/warenwirtschaft",
    action: "Füge Produkte mit aktuellem Bestand hinzu",
    tips: [
      "Mindestbestand für automatische Warnungen setzen",
      "Verfallsdatum für HACCP-Dokumentation wichtig",
      "Lagerort hilft bei der Inventur"
    ],
    highlight: "lager-tabelle",
    category: "einkauf"
  },
  {
    id: "speise_erstellen",
    title: "7. Erste Speise kalkulieren",
    description: "Erstelle dein erstes Gericht mit Rezept und Verkaufspreis. Die Marge wird automatisch berechnet (inkl. 7% MwSt).",
    icon: UtensilsCrossed,
    path: "/speisen",
    action: "Klicke auf 'Neue Speise' und füge Zutaten hinzu",
    tips: [
      "Zutaten mit Mengenangaben hinzufügen",
      "Wareneinsatz sollte max. 30% vom VK sein",
      "Allergene werden automatisch aus Zutaten übernommen"
    ],
    highlight: "add-speise-btn",
    category: "produkte"
  },
  {
    id: "getraenk_erstellen",
    title: "8. Erstes Getränk kalkulieren",
    description: "Erfasse Getränke mit Einkaufs- und Verkaufspreis. Die Marge wird automatisch berechnet (inkl. 19% MwSt).",
    icon: Wine,
    path: "/getraenke",
    action: "Klicke auf 'Neues Getränk' und gib die Preise ein",
    tips: [
      "Flaschengröße und Ausschankgröße unterscheiden",
      "Cocktails können mit Zutaten kalkuliert werden",
      "Getränke haben 19% MwSt (nicht 7% wie Speisen)"
    ],
    highlight: "add-getraenk-btn",
    category: "produkte"
  },
  {
    id: "ki_assistent",
    title: "9. KI-Assistent Preiskalkulation",
    description: "Nutze den KI-Assistenten für optimale Verkaufspreise basierend auf Wareneinsatz, Konkurrenz und Marktdaten.",
    icon: Brain,
    path: "/ki-assistent",
    action: "Frage den KI-Assistenten nach Preisempfehlungen",
    tips: [
      "KI analysiert deine Kalkulationen",
      "Konkurrenzpreise werden berücksichtigt",
      "Preisvorschläge können direkt übernommen werden"
    ],
    highlight: "ki-chat",
    category: "analyse"
  },
  {
    id: "einnahmen_erfassen",
    title: "10. Tagesumsatz erfassen",
    description: "Erfasse deine täglichen Einnahmen getrennt nach Speisen, Getränken und Sonstigem.",
    icon: Euro,
    path: "/einnahmen",
    action: "Trage den heutigen Umsatz ein",
    tips: [
      "Täglich erfassen für genaue Statistiken",
      "Gästeanzahl hilft bei der Pro-Kopf-Analyse",
      "Notizen für besondere Tage (Events, Feiertage)"
    ],
    highlight: "einnahmen-form",
    category: "finanzen"
  },
  {
    id: "fixkosten_erfassen",
    title: "11. Fixkosten eintragen",
    description: "Erfasse alle wiederkehrenden Kosten: Miete, Strom, Versicherungen, Abos. Wichtig für die Rentabilitätsberechnung.",
    icon: Building2,
    path: "/fixkosten",
    action: "Füge deine monatlichen Fixkosten hinzu",
    tips: [
      "Quartals-/Jahreskosten auf Monat umrechnen",
      "Kategorien helfen bei der Auswertung",
      "Vertragsende für Kündigungsfristen notieren"
    ],
    highlight: "add-fixkosten-btn",
    category: "finanzen"
  },
  {
    id: "statistik",
    title: "12. Statistiken analysieren",
    description: "Analysiere deine Geschäftsentwicklung: Umsatztrends, Wareneinsatz, Personalkosten und Gewinn.",
    icon: BarChart3,
    path: "/statistik",
    action: "Wähle verschiedene Zeiträume und vergleiche",
    tips: [
      "Wochen- und Monatsvergleiche nutzen",
      "Schwache Tage identifizieren",
      "Saisonale Trends erkennen"
    ],
    highlight: "statistik-charts",
    category: "analyse"
  },
  {
    id: "haccp_checkliste",
    title: "13. HACCP-Checklisten einrichten",
    description: "Erstelle Hygiene-Checklisten für tägliche Kontrollen. Pflicht nach EU-Verordnung 852/2004.",
    icon: ClipboardCheck,
    path: "/haccp",
    action: "Erstelle eine tägliche Temperatur-Checkliste",
    tips: [
      "Temperaturkontrolle täglich dokumentieren",
      "Reinigungspläne mit Datum abhaken",
      "Protokolle können als PDF exportiert werden"
    ],
    highlight: "haccp-checklisten",
    category: "compliance"
  },
  {
    id: "dokumente_import",
    title: "14. Rechnungen importieren",
    description: "Lade Lieferantenrechnungen hoch. Die KI extrahiert automatisch Positionen und Preise.",
    icon: FileText,
    path: "/import",
    action: "Lade eine Rechnung hoch und prüfe die KI-Erkennung",
    tips: [
      "PDF und Bilder werden unterstützt",
      "KI erkennt Lieferanten automatisch",
      "Positionen können direkt ins Lager übernommen werden"
    ],
    highlight: "upload-zone",
    category: "automatisierung"
  },
  {
    id: "strategie",
    title: "15. Strategie & Ziele setzen",
    description: "Definiere Umsatzziele und verfolge deinen Fortschritt. Erkenne Optimierungspotenziale.",
    icon: Target,
    path: "/strategie",
    action: "Setze dein monatliches Umsatzziel",
    tips: [
      "Realistische Ziele basierend auf Historie",
      "Wöchentliche Meilensteine definieren",
      "Bei Abweichungen Maßnahmen planen"
    ],
    highlight: "strategie-ziele",
    category: "analyse"
  },
  {
    id: "export_berichte",
    title: "16. Berichte exportieren",
    description: "Exportiere Kalkulationen, HACCP-Protokolle und MwSt-Übersichten für deinen Steuerberater.",
    icon: FileText,
    path: "/einstellungen?tab=datenimport",
    action: "Gehe zu Export und lade die MwSt-Jahresübersicht",
    tips: [
      "Excel-Format für Steuerberater ideal",
      "MwSt: 7% Speisen, 19% Getränke",
      "Jahresübersicht für Steuererklärung nutzen"
    ],
    highlight: "export-section",
    category: "export"
  },
  {
    id: "konto",
    title: "17. Konto & Abonnement",
    description: "Unter 'Konto' findest du dein Profil, den aktuellen Abonnement-Status mit Verlängerungsdatum und die Liste der freigeschalteten Module. Hier upgradest du deinen Plan oder verwaltest dein Abo.",
    icon: CreditCard,
    path: "/konto",
    action: "Prüfe deinen Abo-Status und die enthaltenen Module",
    tips: [
      "Upgrade jederzeit möglich — Zahlung über Stripe",
      "In der Testphase hast du 7 Tage vollen Zugriff",
      "Feedback-Button direkt unten rechts in der Übersicht"
    ],
    highlight: "konto-subscription",
    category: "konto"
  },
  {
    id: "feedback",
    title: "18. Feedback senden",
    description: "Hilf uns, MargeVio zu verbessern! Teile deine Erfahrungen und Verbesserungsvorschläge mit uns.",
    icon: MessageSquare,
    path: "/konto",
    action: "Klicke auf 'Feedback senden' unter Weitere Aktionen",
    tips: [
      "Dein Feedback hilft bei der Weiterentwicklung",
      "Feature-Wünsche werden priorisiert",
      "Bug-Reports werden schnell bearbeitet"
    ],
    highlight: "konto-feedback-btn",
    category: "feedback"
  },
  {
    id: "complete",
    title: "Herzlichen Glückwunsch!",
    description: `Du hast das Tutorial abgeschlossen und kennst jetzt alle wichtigen Funktionen von MargeVio. Ich, ${ASSISTANT_NAME}, wünsche dir viel Erfolg mit deinem Betrieb!`,
    icon: PartyPopper,
    path: null, // Kein Redirect während Animation
    action: null,
    tips: [
      "KI-Assistent beantwortet weitere Fragen",
      "Tutorial kann in den Einstellungen neu gestartet werden",
      "Viel Erfolg mit MargeVio!"
    ],
    category: "ende"
  }
];

// Tutorial Provider Komponente
export const TutorialProvider = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [skippedSteps, setSkippedSteps] = useState([]);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showCompletionAnimation, setShowCompletionAnimation] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Lade nur den Fortschritt aus localStorage (NICHT den aktiven Status)
  // Tutorial startet immer bei 0% - wird nur manuell gestartet
  useEffect(() => {
    const saved = localStorage.getItem("margevio_tutorial");
    if (saved) {
      try {
        const data = JSON.parse(saved);
        // Lade nur completed/skipped für die Anzeige in Einstellungen
        setCompletedSteps(data.completed || []);
        setSkippedSteps(data.skipped || []);
        // NICHT automatisch fortsetzen - Tutorial startet immer bei 0%
        // isActive und currentStep werden NICHT geladen
      } catch (e) {
        console.error("Tutorial state load error", e);
      }
    }
  }, []);

  // Speichere Tutorial-Status
  useEffect(() => {
    if (isActive || completedSteps.length > 0) {
      localStorage.setItem("margevio_tutorial", JSON.stringify({
        isActive,
        currentStep: currentStepIndex,
        completed: completedSteps,
        skipped: skippedSteps
      }));
    }
  }, [isActive, currentStepIndex, completedSteps, skippedSteps]);

  const currentStep = TUTORIAL_STEPS[currentStepIndex];

  const startTutorial = () => {
    setIsActive(true);
    setCurrentStepIndex(0);
    setIsMinimized(false);
    setShowCompletionAnimation(false);
    navigate("/dashboard");
  };

  const stopTutorial = () => {
    setIsActive(false);
    setIsMinimized(false);
    setShowCompletionAnimation(false);
  };

  const nextStep = () => {
    // Aktuellen Schritt als erledigt markieren
    if (!completedSteps.includes(currentStep.id)) {
      setCompletedSteps([...completedSteps, currentStep.id]);
    }
    
    if (currentStepIndex < TUTORIAL_STEPS.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      // Navigiere zur nächsten Seite
      if (TUTORIAL_STEPS[nextIndex].path) {
        navigate(TUTORIAL_STEPS[nextIndex].path);
      }
    } else {
      // Tutorial beendet - Zeige Animation
      setShowCompletionAnimation(true);
      
      // Nach 15 Sekunden auf Dashboard weiterleiten
      setTimeout(() => {
        setShowCompletionAnimation(false);
        setIsActive(false);
        navigate("/dashboard");
      }, 15000);
    }
  };

  const prevStep = () => {
    if (currentStepIndex > 0) {
      const prevIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevIndex);
      if (TUTORIAL_STEPS[prevIndex].path) {
        navigate(TUTORIAL_STEPS[prevIndex].path);
      }
    }
  };

  const skipStep = () => {
    if (!skippedSteps.includes(currentStep.id)) {
      setSkippedSteps([...skippedSteps, currentStep.id]);
    }
    nextStep();
  };

  const goToStep = (stepIndex) => {
    setCurrentStepIndex(stepIndex);
    if (TUTORIAL_STEPS[stepIndex].path) {
      navigate(TUTORIAL_STEPS[stepIndex].path);
    }
  };

  const resetTutorial = () => {
    setCompletedSteps([]);
    setSkippedSteps([]);
    setCurrentStepIndex(0);
    setIsActive(false);
    setShowCompletionAnimation(false);
    localStorage.removeItem("margevio_tutorial");
  };

  const value = {
    isActive,
    isMinimized,
    setIsMinimized,
    currentStep,
    currentStepIndex,
    totalSteps: TUTORIAL_STEPS.length,
    completedSteps,
    skippedSteps,
    allSteps: TUTORIAL_STEPS,
    startTutorial,
    stopTutorial,
    nextStep,
    prevStep,
    skipStep,
    goToStep,
    resetTutorial,
    showCompletionAnimation,
    setShowCompletionAnimation,
    assistantName: ASSISTANT_NAME,
    progress: Math.round((completedSteps.length / TUTORIAL_STEPS.length) * 100)
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
      {isActive && !showCompletionAnimation && <TutorialOverlay />}
      {showCompletionAnimation && <CompletionAnimation />}
    </TutorialContext.Provider>
  );
};

// Completion Animation Komponente
const CompletionAnimation = () => {
  const tutorial = useTutorial();
  const [countdown, setCountdown] = useState(15);
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const skipToEnd = () => {
    tutorial.setShowCompletionAnimation(false);
    tutorial.stopTutorial();
    window.location.href = "/dashboard";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-slate-900 via-violet-900/50 to-slate-900">
      {/* Confetti Animation */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-confetti"
            style={{
              left: `${Math.random() * 100}%`,
              top: `-20px`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
            }}
          >
            <div 
              className="w-3 h-3 rounded-sm"
              style={{
                backgroundColor: ['#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899'][Math.floor(Math.random() * 6)],
                transform: `rotate(${Math.random() * 360}deg)`,
              }}
            />
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="relative text-center px-8 animate-in zoom-in-50 duration-700 flex flex-col items-center">
        {/* Hero: Clapping Gian-Luca video with transparent background */}
        <div className="relative mb-6">
          <span
            className="absolute inset-0 -m-10 rounded-full bg-violet-500/30 blur-3xl gianluca-glow pointer-events-none"
            aria-hidden
          />
          <video
            src="/avatar/gianluca-clap.webm"
            autoPlay
            loop
            muted
            playsInline
            className="relative h-72 md:h-96 w-auto drop-shadow-[0_20px_50px_rgba(139,92,246,0.55)]"
            data-testid="completion-clap-video"
          />
        </div>

        {/* Text */}
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 animate-in slide-in-from-bottom-4 duration-500">
          Willkommen bei MargeVio!
        </h1>
        
        <p className="text-xl text-violet-200 mb-2 animate-in slide-in-from-bottom-4 duration-500 delay-150">
          Du hast das Tutorial erfolgreich abgeschlossen
        </p>
        
        <p className="text-lg text-slate-400 mb-8 animate-in slide-in-from-bottom-4 duration-500 delay-300">
          Viel Erfolg mit deinem Betrieb! 🚀
        </p>

        {/* Gian-Luca Nachricht */}
        <div className="max-w-md mx-auto bg-slate-800/80 backdrop-blur rounded-2xl p-6 mb-8 border border-violet-500/30 animate-in slide-in-from-bottom-4 duration-500 delay-500">
          <div className="flex items-center gap-3 mb-3">
            <GianlucaAvatar size="md" state="excited" withGlow />
            <span className="text-emerald-400 font-medium">{ASSISTANT_NAME}</span>
          </div>
          <p className="text-slate-300 text-left">
            &quot;Es war mir eine Freude, dich durch MargeVio zu führen!
            Bei Fragen stehe ich dir jederzeit im KI-Assistenten zur Verfügung. Ciao! 👋&quot;
          </p>
        </div>

        {/* Countdown & Skip */}
        <div className="flex flex-col items-center gap-4 animate-in fade-in duration-500 delay-700">
          <p className="text-slate-400">
            Weiterleitung zum Dashboard in <span className="text-violet-400 font-bold">{countdown}</span> Sekunden
          </p>
          <Button 
            onClick={skipToEnd}
            className="bg-violet-600 hover:bg-violet-500"
          >
            Jetzt zum Dashboard
            <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </div>

      {/* CSS for confetti animation */}
      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti linear forwards;
        }
      `}</style>
    </div>
  );
};

// Tutorial Overlay Komponente
const TutorialOverlay = () => {
  const tutorial = useTutorial();
  const { 
    currentStep, currentStepIndex, totalSteps, 
    nextStep, prevStep, skipStep, stopTutorial,
    isMinimized, setIsMinimized, completedSteps, skippedSteps, allSteps, goToStep,
    assistantName
  } = tutorial;

  const [showStepList, setShowStepList] = useState(false);

  if (!currentStep) return null;

  const Icon = currentStep.icon;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const isCompleteStep = currentStep.id === "complete";

  // Kategorie-Farben
  const categoryColors = {
    start: "from-violet-500 to-purple-600",
    grundlagen: "from-emerald-500 to-teal-600",
    personal: "from-blue-500 to-indigo-600",
    einkauf: "from-orange-500 to-amber-600",
    produkte: "from-pink-500 to-rose-600",
    finanzen: "from-green-500 to-emerald-600",
    analyse: "from-cyan-500 to-blue-600",
    compliance: "from-red-500 to-rose-600",
    automatisierung: "from-purple-500 to-violet-600",
    export: "from-slate-500 to-zinc-600",
    feedback: "from-amber-500 to-yellow-600",
    ende: "from-yellow-500 to-orange-600"
  };

  const gradientClass = categoryColors[currentStep.category] || "from-violet-500 to-purple-600";

  // Minimierte Ansicht
  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsMinimized(false)}
          className="flex items-center gap-3 pl-2 pr-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105"
          data-testid="tutorial-minimized-btn"
        >
          <GianlucaAvatar size="sm" state="idle" />
          <span className="font-medium">{assistantName} fortsetzen</span>
          <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm">
            {currentStepIndex + 1}/{totalSteps}
          </span>
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Backdrop - Nicht verschwommen */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setIsMinimized(true)} />

      {/* Full-Body Gian-Luca – Desktop only, stands next to the tutorial card */}
      <div
        className="hidden lg:block fixed bottom-0 right-[440px] z-40 pointer-events-none animate-in slide-in-from-right-10 fade-in duration-500"
        aria-hidden
      >
        <GianlucaAvatar
          variant="fullbody"
          state={isCompleteStep ? "excited" : "speaking"}
          withGlow
          height="min(70vh, 560px)"
          testId="tutorial-avatar-fullbody"
        />
      </div>

      {/* Tutorial Card */}
      <div className="fixed bottom-4 right-4 left-4 md:left-auto md:w-[420px] z-50 animate-in slide-in-from-bottom-4 duration-300">
        <div className="bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
          {/* Header mit Gradient */}
          <div className={`bg-gradient-to-r ${gradientClass} p-4`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <GianlucaAvatar
                  size="md"
                  state={isCompleteStep ? "excited" : "speaking"}
                  withGlow
                  testId="tutorial-avatar"
                />
                <div>
                  <h3 className="text-white font-bold text-lg leading-tight flex items-center gap-2">
                    <Icon size={16} className="text-white/90 shrink-0" />
                    {currentStep.title}
                  </h3>
                  <p className="text-white/80 text-sm">
                    {assistantName} • Schritt {currentStepIndex + 1} von {totalSteps}
                  </p>
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setIsMinimized(true)}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                  title="Minimieren"
                >
                  <ChevronRight size={18} className="text-white rotate-90" />
                </button>
                <button
                  onClick={stopTutorial}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                  title="Beenden"
                >
                  <X size={18} className="text-white" />
                </button>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-3 h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: `${((currentStepIndex + 1) / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            <p className="text-slate-300 text-sm leading-relaxed mb-4">
              {currentStep.description}
            </p>

            {/* Action Hinweis */}
            {currentStep.action && (
              <div className="bg-slate-700/50 rounded-lg p-3 mb-4 border border-slate-600">
                <div className="flex items-start gap-2">
                  <ArrowRight size={16} className="text-violet-400 mt-0.5 shrink-0" />
                  <p className="text-violet-300 text-sm font-medium">{currentStep.action}</p>
                </div>
              </div>
            )}

            {/* Tipps */}
            {currentStep.tips && currentStep.tips.length > 0 && (
              <div className="space-y-2 mb-4">
                <p className="text-slate-400 text-xs font-medium uppercase tracking-wide">Tipps</p>
                {currentStep.tips.map((tip, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <CircleDot size={12} className="text-slate-500 mt-1 shrink-0" />
                    <p className="text-slate-400 text-sm">{tip}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Step List Toggle */}
            <button
              onClick={() => setShowStepList(!showStepList)}
              className="w-full text-left text-sm text-slate-400 hover:text-slate-300 flex items-center gap-2 mb-3"
            >
              <ChevronRight size={14} className={`transition-transform ${showStepList ? "rotate-90" : ""}`} />
              Alle Schritte anzeigen
            </button>

            {/* Step List */}
            {showStepList && (
              <div className="mb-4 max-h-48 overflow-y-auto space-y-1 pr-2 custom-scrollbar">
                {allSteps.map((step, idx) => {
                  const StepIcon = step.icon;
                  const isCompleted = completedSteps.includes(step.id);
                  const isSkipped = skippedSteps.includes(step.id);
                  const isCurrent = idx === currentStepIndex;
                  
                  return (
                    <button
                      key={step.id}
                      onClick={() => goToStep(idx)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors ${
                        isCurrent 
                          ? "bg-violet-600/30 text-violet-300" 
                          : isCompleted 
                            ? "text-emerald-400 hover:bg-slate-700/50" 
                            : isSkipped
                              ? "text-slate-500 hover:bg-slate-700/50"
                              : "text-slate-400 hover:bg-slate-700/50"
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                        isCompleted ? "bg-emerald-500/20" : isCurrent ? "bg-violet-500/20" : "bg-slate-700"
                      }`}>
                        {isCompleted ? (
                          <Check size={12} className="text-emerald-400" />
                        ) : (
                          <span className="text-xs">{idx + 1}</span>
                        )}
                      </div>
                      <span className="text-xs truncate">{step.title.replace(/^\d+\.\s*/, "")}</span>
                      {isSkipped && <span className="text-[10px] text-slate-600 ml-auto">übersprungen</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center gap-2">
              {!isFirstStep && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={prevStep}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <ChevronLeft size={16} />
                  Zurück
                </Button>
              )}
              
              <div className="flex-1" />
              
              {!isCompleteStep && !isLastStep && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={skipStep}
                  className="text-slate-400 hover:text-slate-300"
                >
                  Überspringen
                  <SkipForward size={14} className="ml-1" />
                </Button>
              )}
              
              <Button
                size="sm"
                onClick={nextStep}
                className={`bg-gradient-to-r ${gradientClass} hover:opacity-90`}
              >
                {isCompleteStep ? (
                  <>
                    <Check size={16} className="mr-1" />
                    Abschließen
                  </>
                ) : (
                  <>
                    Weiter
                    <ChevronRight size={16} className="ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Tutorial Start Button für Header/Sidebar
export const TutorialStartButton = ({ variant = "default" }) => {
  const tutorial = useTutorial();
  
  if (!tutorial) return null;
  
  const { startTutorial, progress, isActive, assistantName } = tutorial;

  if (isActive) return null;

  if (variant === "compact") {
    return (
      <button
        onClick={startTutorial}
        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-violet-400 hover:bg-slate-700/50 rounded-lg transition-colors"
        data-testid="tutorial-start-btn"
      >
        <Play size={16} />
        <span>{assistantName}</span>
        {progress > 0 && progress < 100 && (
          <span className="text-xs text-violet-400">({progress}%)</span>
        )}
      </button>
    );
  }

  return (
    <button
      onClick={startTutorial}
      className="flex items-center gap-3 w-full px-4 py-3 bg-gradient-to-r from-violet-600/20 to-purple-600/20 hover:from-violet-600/30 hover:to-purple-600/30 border border-violet-500/30 rounded-xl text-left transition-all"
      data-testid="tutorial-start-btn"
    >
      <div className="w-10 h-10 bg-gradient-to-r from-violet-500 to-purple-500 rounded-lg flex items-center justify-center">
        <Play size={20} className="text-white" />
      </div>
      <div>
        <p className="text-white font-medium">{assistantName} starten</p>
        <p className="text-slate-400 text-sm">
          {progress > 0 && progress < 100 
            ? `${progress}% abgeschlossen` 
            : "Schritt-für-Schritt Anleitung"}
        </p>
      </div>
    </button>
  );
};

// Tutorial Reset Button für Einstellungen
export const TutorialResetButton = () => {
  const tutorial = useTutorial();
  
  if (!tutorial) return null;
  
  const { resetTutorial, startTutorial, progress, assistantName } = tutorial;

  const handleReset = () => {
    resetTutorial();
    startTutorial();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-medium">{assistantName} Tutorial</h3>
          <p className="text-slate-400 text-sm">
            {progress === 100 
              ? "Tutorial abgeschlossen ✓" 
              : progress > 0 
                ? `${progress}% abgeschlossen`
                : "Noch nicht gestartet"}
          </p>
        </div>
        <Button
          onClick={handleReset}
          variant="outline"
          className="border-violet-500 text-violet-400 hover:bg-violet-500/20"
        >
          <RefreshCw size={16} className="mr-2" />
          Neu starten
        </Button>
      </div>
    </div>
  );
};

export default TutorialProvider;
